import { useEffect, useMemo, useRef } from 'react';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import {
  Watchable,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { WatchablesFactory } from './watchables-factory';
import { useWatchablesFactory } from './watchables-factory-context';

/**
 * Subscribes to a `ResourceUpdated`-backed event watchable for the lifetime of
 * the calling component, so DB changes stream into the UI without a manual
 * refetch. The watchable is rebuilt whenever `key` changes (e.g. the set of ids
 * being summarized) and unsubscribed on unmount.
 *
 * Use for standalone hooks that aren't owned by a `BaseModel` (which would
 * otherwise own + dispose the watchable). `build` reads the latest closure on
 * each rebuild via a ref, so only `key` needs to capture the inputs that should
 * trigger a fresh fetch.
 *
 * Pass `opts.enabled: false` to hold a static `disabledValue` with no event
 * subscription at all — for lazy hooks (e.g. a collapsed accordion row) that
 * shouldn't listen or fetch until activated. Flipping `enabled` rebuilds.
 */
export function useResourceUpdatedWatchable<T>(
  key: string,
  build: (factory: WatchablesFactory) => ReadonlyEventWatchable<T>,
  opts?: { enabled?: boolean; disabledValue?: T }
): T {
  const factory = useWatchablesFactory();
  const buildRef = useRef(build);
  buildRef.current = build;
  const enabled = opts?.enabled ?? true;
  const disabledValueRef = useRef(opts?.disabledValue);
  disabledValueRef.current = opts?.disabledValue;

  const watchable = useMemo<ReadonlyWatchable<T>>(
    () =>
      enabled
        ? buildRef.current(factory)
        : Watchable.fromValue(disabledValueRef.current as T),
    // Keyed on `key`/`enabled` only — `build`/`disabledValue` read fresh via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [factory, key, enabled]
  );

  useEffect(() => {
    // Only the event watchable (enabled) holds a subscription to tear down; the
    // static disabled watchable has nothing to release.
    if (!enabled) {
      return;
    }
    const eventWatchable = watchable as ReadonlyEventWatchable<T>;
    return () => eventWatchable.unsubscribe();
  }, [watchable, enabled]);

  return useWatchableValue(watchable);
}

/** Shared payload shape for filtering `ResourceUpdated` events by resource type. */
export type ResourceUpdatedFilterPayload = {
  resourceType?: string;
  /**
   * Workflow only: 'content' (create/rename/move/archive) vs 'run' (a
   * high-frequency run or trigger tick). Absent on events from an older server
   * that predates the field.
   */
  changeType?: string;
};

/** True for workflow / workflow_trigger `ResourceUpdated` events. */
export function isWorkflowResourceEvent(
  payload: ResourceUpdatedFilterPayload | undefined
): boolean {
  return (
    payload?.resourceType === 'workflow' ||
    payload?.resourceType === 'workflow_trigger'
  );
}

/**
 * True only for a positively-tagged workflow content change (create / rename /
 * move / archive). Run and trigger ticks carry `changeType: 'run'` and are
 * high-frequency, so high-frequency subscribers use this to refresh on real
 * content changes without that firehose. The check is positive (`=== 'content'`)
 * on purpose: an event with a missing changeType comes from a server that
 * predates the field, and during that rollout window we would rather skip the
 * refetch (matching the prior #9795 behavior) than re-emit the firehose.
 * Excludes `workflow_trigger`, which never changes the set of workflows.
 */
export function isWorkflowContentEvent(
  payload: ResourceUpdatedFilterPayload | undefined
): boolean {
  return (
    payload?.resourceType === 'workflow' && payload.changeType === 'content'
  );
}

/** True for dataset `ResourceUpdated` events. */
export function isDatasetResourceEvent(
  payload: ResourceUpdatedFilterPayload | undefined
): boolean {
  return payload?.resourceType === 'dataset';
}

/** True for agent / document / folder / trigger / dataset `ResourceUpdated` events. */
export function isCocoMentionResourceEvent(
  payload: ResourceUpdatedFilterPayload | undefined
): boolean {
  const rt = payload?.resourceType;
  return (
    rt === 'agent' || rt === 'document' || rt === 'folder' || rt === 'dataset'
  );
}
